import {
  combineReducers,
  configureStore,
  getDefaultMiddleware,
} from "@reduxjs/toolkit";
import createSagaMiddleware from "redux-saga";
import { createBrowserHistory } from "history";
import { connectRouter, routerMiddleware } from "connected-react-router";
import storage from "redux-persist/lib/storage";

import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";

import appReducer from "./appSlice";
import authReducer from "./authSlice";
import transactionsReducer from "./transactionsSlice";
import applicationsReducer from "./applicationsSlice";
import accountsReducer from "./accountsSlice";
import usersReducer from "./usersSlice";
import valuesReducer from "./valuesSlice";
import boardingReducer from "./boardingSlice";
import customersReducer from "./customersSlice";
import notificationsReducer from "./notificationsSlice";
import banksReducer from "./bankSlice";
import cashManagementReducer from "./cashManagementSlice";
import cashManagementProviderReducer from "./cashManagementProviderSlice";
import cashManagementProviderCustomerConfigReducer from "./cashManagementProviderCustomerConfigSlice";
import cashManagementNotificationReducer from "./cashManagementNotificationSlice";
import paymentsProvidersReducer from "./paymentsProvidersSlice";
import customersPaymentsProvidersReducer from "./customersPaymentsProvidersSlice";
import rootSaga from "../sagas";

export const history = createBrowserHistory();

const sagaMiddleware = createSagaMiddleware();

const authPersistConfig = {
  key: "auth",
  storage,
  whitelist: [
    "email",
    "accessToken",
    "refreshToken",
    "expirationTimestamp",
    "userId",
    "customerId",
    "roles",
  ],
  blacklist: [],
};

const appPersistConfig = {
  key: "app",
  storage,
  whitelist: ["environment", "impersonatedCustomer"],
  blacklist: [],
};

const rootReducer = combineReducers({
  router: connectRouter(history),
  app: persistReducer(appPersistConfig, appReducer),
  auth: persistReducer(authPersistConfig, authReducer),
  transactions: transactionsReducer,
  applications: applicationsReducer,
  accounts: accountsReducer,
  users: usersReducer,
  boarding: boardingReducer,
  values: valuesReducer,
  customers: customersReducer,
  customersPaymentsProviders: customersPaymentsProvidersReducer,
  notifications: notificationsReducer,
  banks: banksReducer,
  cashManagement: cashManagementReducer,
  cashManagementProvider: cashManagementProviderReducer,
  cashManagementProviderCustomerConfig: cashManagementProviderCustomerConfigReducer,
  cashManagementNotification: cashManagementNotificationReducer,
  paymentsProviders: paymentsProvidersReducer,
});

const store = configureStore({
  reducer: rootReducer,
  devTools: process.env.NODE_ENV !== "production",
  middleware: [
    ...getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
    sagaMiddleware,
    routerMiddleware(history),
  ],
});

sagaMiddleware.run(rootSaga);

export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;

export default store;
